import {createReducer} from "redux-act";
import {CardType, RequestState} from "modules/enums";
import {
	IGetSettingsResponse,
	IHelpPagesResponse,
	requestAchievements,
	requestAchievementsFailed,
	requestAchievementsSuccess,
	requestAvatars,
	requestAvatarsFailed,
	requestAvatarsSuccess,
	requestCountries,
	requestCountriesFailed,
	requestCountriesSuccess,
	requestHelpPages,
	requestHelpPagesFailed,
	requestHelpPagesSuccess,
	requestSettings,
	requestSettingsFailed,
	requestSettingsSuccess,
	requestTeams,
	requestTeamsFailed,
	requestTeamsSuccess,
	TCountries,
} from "modules/actions";

export interface ICard {
	id: number;
	position: number;
	gameLogoEn: string;
	gameLogoFr: string;
	gameDescription: Record<string, string>;
	sponsorLogoEn: string | null;
	sponsorLogoFr: string | null;
	sponsorLogoText: Record<string, string>;
	sponsorLogoLink: Record<string, string>;
	btnMsg: Record<string, string>;
	gameLink: Record<string, string>;
	status: CardType;
	background: string;
}

export interface ITeam {
	id: number;
	fullName: string;
	shortName: string;
	abbr: string;
}

export interface IHelp {
	id: number;
	type: string;
	title: string;
	text: string;
	game?: string;
}

export interface IAvatar {
	id: number;
	name: Record<string, string>;
	avatar: string;
}

export interface IAchievement {
	name: string;
	game: string;
	progress: number;
	earned: boolean;
	earnedAt: null | string;
}

export interface IJSONsReducer {
	requestState: RequestState;
	teamsRequestState: RequestState;
	helpsRequestState: RequestState;
	avatarsRequestState: RequestState;
	achievementsRequestState: RequestState;
	countriesRequestState: RequestState;
	background: string;
	cards: ICard[];
	teams: ITeam[];
	avatars: IAvatar[];
	achievements: IAchievement[];
	countries: TCountries;
	guidelines: IHelp[];
	faqs: IHelp[];
	terms: IHelp[];
}

const initialState: IJSONsReducer = {
	requestState: RequestState.IDLE,
	teamsRequestState: RequestState.IDLE,
	helpsRequestState: RequestState.IDLE,
	avatarsRequestState: RequestState.IDLE,
	achievementsRequestState: RequestState.IDLE,
	countriesRequestState: RequestState.IDLE,
	background: "",
	cards: [],
	teams: [],
	avatars: [],
	achievements: [],
	countries: {},
	guidelines: [],
	faqs: [],
	terms: [],
};

const onRequestSettings = (state: IJSONsReducer) => ({
	...state,
	requestState: RequestState.Requested,
});

const onRequestSettingsSuccess = (state: IJSONsReducer, payload: IGetSettingsResponse) => ({
	...state,
	requestState: RequestState.Received,
	background: payload.background,
	cards: payload.cards,
});

const onRequestSettingsFailed = (state: IJSONsReducer) => ({
	...state,
	requestState: RequestState.Received,
});

const onRequestTeams = (state: IJSONsReducer) => ({
	...state,
	teamsRequestState: RequestState.Requested,
});

const onRequestTeamsSuccess = (state: IJSONsReducer, payload: ITeam[]) => ({
	...state,
	teamsRequestState: RequestState.Received,
	teams: payload,
});

const onRequestTeamsFailed = (state: IJSONsReducer) => ({
	...state,
	teamsRequestState: RequestState.Received,
});

const onRequestHelpPages = (state: IJSONsReducer) => ({
	...state,
	helpsRequestState: RequestState.Requested,
});

const onRequestHelpPagesSuccess = (state: IJSONsReducer, payload: IHelpPagesResponse) => ({
	...state,
	helpsRequestState: RequestState.Received,
	faqs: payload.faqs,
	terms: payload.terms,
	guidelines: payload.guidelines,
});

const onRequestHelpPagesFailed = (state: IJSONsReducer) => ({
	...state,
	helpsRequestState: RequestState.Received,
});

const onRequestAvatars = (state: IJSONsReducer) => ({
	...state,
	avatarsRequestState: RequestState.Requested,
});

const onRequestAvatarsSuccess = (state: IJSONsReducer, payload: IAvatar[]) => ({
	...state,
	avatarsRequestState: RequestState.Received,
	avatars: payload,
});

const onRequestAvatarsFailed = (state: IJSONsReducer) => ({
	...state,
	avatarsRequestState: RequestState.Received,
});

const onRequestAchievements = (state: IJSONsReducer) => ({
	...state,
	achievementsRequestState: RequestState.Requested,
});

const onRequestAchievementsSuccess = (state: IJSONsReducer, payload: IAchievement[]) => ({
	...state,
	achievementsRequestState: RequestState.Received,
	achievements: payload,
});

const onRequestAchievementsFailed = (state: IJSONsReducer) => ({
	...state,
	achievementsRequestState: RequestState.Received,
});

const onRequestCountries = (state: IJSONsReducer) => ({
	...state,
	countriesRequestState: RequestState.Requested,
});

const onRequestCountriesSuccess = (state: IJSONsReducer, payload: TCountries) => ({
	...state,
	countriesRequestState: RequestState.Received,
	countries: payload,
});

const onRequestCountriesFailed = (state: IJSONsReducer) => ({
	...state,
	countriesRequestState: RequestState.Received,
});

export const jsons = createReducer<IJSONsReducer>({}, initialState)
	.on(requestSettings, onRequestSettings)
	.on(requestSettingsSuccess, onRequestSettingsSuccess)
	.on(requestSettingsFailed, onRequestSettingsFailed)
	.on(requestTeams, onRequestTeams)
	.on(requestTeamsSuccess, onRequestTeamsSuccess)
	.on(requestTeamsFailed, onRequestTeamsFailed)
	.on(requestHelpPages, onRequestHelpPages)
	.on(requestHelpPagesSuccess, onRequestHelpPagesSuccess)
	.on(requestHelpPagesFailed, onRequestHelpPagesFailed)
	.on(requestAvatars, onRequestAvatars)
	.on(requestAvatarsSuccess, onRequestAvatarsSuccess)
	.on(requestAvatarsFailed, onRequestAvatarsFailed)
	.on(requestAchievements, onRequestAchievements)
	.on(requestAchievementsSuccess, onRequestAchievementsSuccess)
	.on(requestAchievementsFailed, onRequestAchievementsFailed)
	.on(requestCountries, onRequestCountries)
	.on(requestCountriesSuccess, onRequestCountriesSuccess)
	.on(requestCountriesFailed, onRequestCountriesFailed);
